import { useRef, useCallback, useState } from 'react';
import isEqual from 'fast-deep-equal';

import { EnumVerifyPayeeMatch, type IVerifyPayeeRequest, useApiVOP } from '../api';
import { getVopType } from 'src/Utilities/vop';

export const usePayeeVerification = () => {
  const previousValuesRef = useRef<IVerifyPayeeRequest | null>(null);
  const fromNameRef = useRef<null | string>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<EnumVerifyPayeeMatch | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);

  const { mutateAsync } = useApiVOP.postVOP();

  const runMutation = useCallback(
    async (vals: IVerifyPayeeRequest) => {
      try {
        const response = await mutateAsync(vals);
        const VopResponseType = getVopType(response.PartyNameMatch, response.Details);

        setStatus(VopResponseType || null);
        setCorrelationId(response.RequestId);

        if (response?.MatchedName) {
          fromNameRef.current = response.MatchedName;
        } else {
          fromNameRef.current = null;
        }
      } catch (error: any) {
        console.error('Payee verification failed: ', error);

        if (error?.response?.data?.PartyNameMatch) {
          const VopResponseType = getVopType(error.response.data?.PartyNameMatch, error.response.data?.Details);

          setStatus(VopResponseType || null);
          setCorrelationId(error.response.data.RequestId);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [mutateAsync, setIsLoading]
  );

  const onChangeVop = (values: IVerifyPayeeRequest) => {
    if (!values?.ClientName?.trim() || !values?.Iban?.trim()) {
      return;
    }

    if (fromNameRef.current?.trim().toLowerCase() === values.ClientName.trim().toLowerCase()) {
      return;
    }

    if (!isEqual(previousValuesRef.current, values)) {
      setIsLoading(true);
      previousValuesRef.current = values;

      runMutation(values);
    }
  };

  return {
    status,
    matchedName: fromNameRef.current,
    correlationId,
    isLoading,
    onChangeVop,
  };
};
